import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { getUserName } from '../services/auth';
import { Button } from './index';
import { Camera, X } from 'lucide-react';

interface PhotoUploadProps {
  propertyId: string;
  onAddPhoto: (url: string, addedBy: string) => Promise<void>;
  disabled?: boolean;
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic';
const ACCEPTED_TYPES_SET = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic']);
const MAX_IMAGE_SIZE = 25 * 1024 * 1024;

export function PhotoUpload({ propertyId, onAddPhoto, disabled }: PhotoUploadProps) {
  const { t } = useTranslation();
  const [pendingFiles, setPendingFiles] = useState<{ file: File; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback((files: File[]) => {
    setError(null);
    const imageFiles = files.filter((f) => ACCEPTED_TYPES_SET.has(f.type));
    if (imageFiles.length === 0) {
      setError(t('app.photoUpload.unsupportedFormat'));
      return;
    }

    const oversized = imageFiles.filter((f) => f.size > MAX_IMAGE_SIZE);
    if (oversized.length > 0) {
      setError(`${oversized.map((f) => f.name).join(', ')} ${t('app.photoUpload.sizeLimitExceeded')}`);
    }

    const valid = imageFiles.filter((f) => f.size <= MAX_IMAGE_SIZE);
    if (valid.length === 0) return;

    const newItems = valid.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPendingFiles((prev) => [...prev, ...newItems]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) processFiles(files);
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    const authorName = (await getUserName()) ?? 'Unknown';

    setUploading(true);
    setError(null);
    try {
      for (let i = 0; i < pendingFiles.length; i++) {
        setUploadProgress(t('app.photoUpload.uploadProgress', { current: i + 1, total: pendingFiles.length }));
        const url = await api.uploadImage(propertyId, pendingFiles[i].file);
        await onAddPhoto(url, authorName);
      }
      pendingFiles.forEach((img) => URL.revokeObjectURL(img.preview));
      setPendingFiles([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.photoUpload.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  return (
    <div
      className={`photo-upload ${dragActive ? 'photo-upload-drag' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        onChange={handleFilesSelected}
        style={{ display: 'none' }}
      />

      {pendingFiles.length === 0 ? (
        <button
          type="button"
          className="photo-upload-trigger"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          <Camera size={16} />
          {t('app.photoUpload.addPhotos')}
        </button>
      ) : (
        <>
          <div className="pending-images">
            {pendingFiles.map((img, i) => (
              <div key={i} className="pending-image">
                <img src={img.preview} alt={`Upload ${i + 1}`} />
                <button type="button" className="pending-image-remove" onClick={() => removeFile(i)} disabled={uploading} aria-label={t('app.photoUpload.removeImage')}>
                  <X size={12} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          <div className="photo-upload-actions">
            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {t('common.more')}
            </Button>
            <Button size="sm" onClick={handleUpload} disabled={disabled || uploading}>
              {uploadProgress ?? t('app.photoUpload.uploadButton', { count: pendingFiles.length })}
            </Button>
          </div>
        </>
      )}

      {dragActive && <div className="drag-overlay">{t('app.photoUpload.dropOverlay')}</div>}
      {error && <div className="upload-error">{error}</div>}
    </div>
  );
}
